import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Config } from '../config/config';
import { Subject, Observable, throwError, of } from 'rxjs';
import { map, takeUntil, catchError, finalize, switchMap, tap } from 'rxjs/operators';
import { HttPromises } from 'src/app/core/service/httPromises.service';
import { WebsocketService } from 'src/app/core/service/websocket.service';
import { StateObservable, StructureObservable } from './observable.service';
import { ToastService } from 'src/app/toast/toast.service';
import { StructureEvent } from 'src/app/core/models/websocket/structure';

export interface StructuringStatus {
    isStructuring: boolean;
    tableId: string | null;
    jobCount: number;
}

@Injectable({
    providedIn: 'root',
})
export class TableExtractorService {

    apiUrl = Config.backend_url + '/table-extractor';
    private cancelRequestSubject = new Subject<void>();

    constructor(
        private httpClient: HttpClient,
        private httPromises: HttPromises,
        private websocketService: WebsocketService,
        private toastService: ToastService,
        private stateObservable: StateObservable,
        private structureObservable: StructureObservable
    ) { }

    private showSuccess(message:any, permanent?: boolean) {
        this.toastService.show('info', message, {
            classname: 'bg-white text-dark position-fixed bottom-0 end-0 mb-7 me-3',
            permanent: permanent
        });
    }

    private showError(error:any, permanent?: boolean) {
        this.toastService.showError("Structurization error",error, permanent);
    }

    private propagateEvent(status: StructureEvent['status'], tableId?: string, fileId?: string) {
        const event:StructureEvent = { status, tableId, fileId };
        this.structureObservable.registerNewUpdate(event);
    }

    getStructuringStatus(): Observable<StructuringStatus> {
        return this.httpClient.get<StructuringStatus>(`${this.apiUrl}/structuring-status`).pipe(
            catchError(error => {
                console.error('Error getting structuring status:', error);
                return throwError(error);
            })
        );
    }

    cancelJob(): Observable<string> {
        return this.httpClient.delete<string>(`${this.apiUrl}/cancel-job`).pipe(
            tap(() => {
                this.showSuccess('Cancelling all structuring jobs...');
                this.stateObservable.setLoading(false);
                this.propagateEvent('cancelled');
            }),
            catchError(error => {
                console.error('Error cancelling jobs:', error);
                this.showError('Failed to cancel jobs');
                return throwError(error);
            })
        );
    }


    cancelRequest() {
        this.cancelJob().subscribe({
            next: () => {
                console.log('Jobs cancelled successfully via API');
            },
            error: (error) => {
                console.error('Failed to cancel jobs via API, falling back to local cancellation:', error);
                this.cancelRequestSubject.next();
                this.stateObservable.setLoading(false);
                this.propagateEvent('cancelled');
            }
        });
    }

    request(tableId: string, fileIds: string | string[], jsonSchema?: any, clearExistingData?: boolean): Observable<any> {
        this.stateObservable.setLoading(true);
        this.propagateEvent('all-start');

        const requestURI = `${this.apiUrl}/process-file`;

        return this.websocketService.connect().pipe(
            switchMap(socketId => {
                if (!socketId) {
                    console.error("WebSocket connection failed or no socketId received.");
                    this.stateObservable.setLoading(false);
                    return of(null);
                }
                const headers = {};
                const requestBody: {
                    fileIds?: string[];
                    jsonSchema?: any;
                    tableId?: string;
                    clearExistingData?: boolean;
                } = {};
                if (socketId) {
                    Object.assign(headers, {'x-socket-id': socketId});
                }
                if (fileIds) {
                    // Convert single string to array if needed
                    const ids = Array.isArray(fileIds) ? fileIds : [fileIds];
                    requestBody.fileIds = ids;
                }
                if (jsonSchema) {
                    requestBody.jsonSchema = jsonSchema;
                }
                if (tableId) {
                    requestBody.tableId = tableId;
                }
                if (clearExistingData !== undefined) {
                    requestBody.clearExistingData = clearExistingData;
                }
                return this.httpClient.put(requestURI, requestBody, {
                    headers: headers
                }).pipe(
                    takeUntil(this.cancelRequestSubject),
                    map(res => {
                      return res;
                    }),
                    catchError(error => {
                      console.error('Error during HTTP request:', error);
                      return throwError(error);
                    }),
                    finalize(() => {
                      this.stateObservable.setLoading(false);
                    })
                );
            })
        );
    }
}
